import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productAdminService } from '../../services/api';
import { useToast } from '../../store/ToastContext';
import { formatDate, formatNumber } from '../../utils/helpers';
import Button from '../../components/Button';
import UsageMeter from '../../components/UsageMeter';
import LimitEditor from './LimitEditor';
import styles from './ProductAdmin.module.css';
import ui from './ProductAdminDashboard.module.css';
import t from './AdminTable.module.css';
import { SectionHeading } from './AdminWidgets';
import { ActivityIcon, FileTextIcon, UsersIcon } from './ProductAdminIcons';

const TOKEN_STATUS = { used: 'Activated', active: 'Not activated', expired: 'Expired', revoked: 'Revoked' };

function tomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function OrganizationLimit({ subscription, onSaveLimit, onMakeUnlimited }) {
  const docs = subscription.documents;
  const isTrial = subscription.is_trial;
  const [settingNumber, setSettingNumber] = useState(false);

  if (docs.unlimited && !settingNumber) {
    return (
      <div className={styles.limitPanel}>
        <label>Organization document limit</label>
        <span className={styles.bigValue}><span className={`${t.badge} ${t.badgeSuccess}`}>Unlimited</span></span>
        <small>No document limit until the plan ends. {formatNumber(docs.documents_used)} documents signed so far.</small>
        <div className={styles.panelActions}>
          <Button size="sm" variant="secondary" onClick={() => setSettingNumber(true)}>Set a number instead</Button>
        </div>
      </div>
    );
  }

  const minimum = isTrial || docs.unlimited
    ? docs.documents_used
    : Math.max(docs.allocated || 0, docs.documents_used);

  return (
    <div className={styles.limitPanel}>
      <label>Organization document limit</label>
      <LimitEditor
        value={docs.unlimited ? null : docs.document_limit}
        min={minimum}
        onSave={async (value) => { if (await onSaveLimit(subscription, value)) setSettingNumber(false); }}
        label="Organization document limit"
      />
      <small>
        {docs.unlimited
          ? `Enter the total the organization may sign. It must cover what its licences already have.`
          : isTrial
            ? 'Free trial limit for this organization.'
            : `Given to licences: ${formatNumber(docs.allocated)} · Not given yet: ${formatNumber(docs.unallocated)}.`}
      </small>
      <div className={styles.panelActions}>
        {docs.unlimited ? (
          <Button size="sm" variant="ghost" onClick={() => setSettingNumber(false)}>Keep unlimited</Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => onMakeUnlimited(subscription)}>Give unlimited documents</Button>
        )}
      </div>
    </div>
  );
}

function PlanValidity({ subscription, onSaveValidity }) {
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(null);
  const active = subscription.status === 'active';

  const save = async (key, payload) => {
    setSaving(key);
    try {
      if (await onSaveValidity(subscription, payload)) setEndDate('');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className={styles.limitPanel}>
      <label>Plan validity</label>
      <span className={styles.bigValue}>
        {formatDate(subscription.expiry_datetime)}
        <small className={active ? styles.muted : styles.expiredText}>
          {active ? `${formatNumber(subscription.days_remaining)} days left` : 'Ended'}
        </small>
      </span>
      <div className={styles.panelActions}>
        <Button size="sm" variant="secondary" loading={saving === 1} onClick={() => save(1, { extend_years: 1 })}>
          +1 year
        </Button>
        <Button size="sm" variant="secondary" loading={saving === 2} onClick={() => save(2, { extend_years: 2 })}>
          +2 years
        </Button>
      </div>
      <div className={ui.limitEditor}>
        <input
          type="date"
          min={tomorrow()}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className={ui.limitInput}
          aria-label="New end date"
        />
        <Button size="sm" disabled={!endDate} loading={saving === 'date'} onClick={() => save('date', { expiry_date: endDate })}>
          Set end date
        </Button>
      </div>
      <small>
        {active ? 'Years are added to the current end date.' : 'Years are added from today and the plan becomes active again.'}{' '}
        Computers pick up the new date the next time the desktop app starts online.
      </small>
    </div>
  );
}

function SubscriptionLimits({ subscription, tokenRows, onSaveLimit, onMakeUnlimited, onSaveValidity, onSaveLicence, onViewUsage }) {
  const docs = subscription.documents;
  const isTrial = subscription.is_trial;

  return (
    <section className={ui.panel}>
      <SectionHeading
        tone="teal"
        icon={<FileTextIcon />}
        title={`${subscription.plan_name || 'Plan'}${isTrial ? '' : ' Plan'} · Document Limits`}
        text={`${subscription.licences} licence${subscription.licences === 1 ? '' : 's'} · valid ${formatDate(subscription.subscription_datetime)} – ${formatDate(subscription.expiry_datetime)}${subscription.owner ? ` · ${subscription.owner.email}` : ''}`}
        action={(
          <span className={`${t.badge} ${subscription.status === 'active' ? (isTrial ? t.badgeInfo : t.badgeSuccess) : t.badgeDanger}`}>
            {subscription.status === 'active' ? (isTrial ? 'Free trial' : 'Active') : 'Expired'}
          </span>
        )}
      />

      <div className={styles.limitGrid3}>
        <div className={styles.limitPanel}>
          <UsageMeter
            size="lg"
            label="Documents signed by all licences"
            used={docs.documents_used}
            limit={docs.document_limit}
            unlimited={docs.unlimited}
            caption={docs.unlimited ? 'No limit until the plan ends' : `${formatNumber(docs.documents_remaining)} documents left`}
          />
        </div>
        <OrganizationLimit subscription={subscription} onSaveLimit={onSaveLimit} onMakeUnlimited={onMakeUnlimited} />
        <PlanValidity subscription={subscription} onSaveValidity={onSaveValidity} />
      </div>

      <div className={t.wrap}>
        <table className={t.table}>
          <thead>
            <tr>
              <th>Licence</th>
              <th className={`${t.wStatus} ${t.hideXs}`}>Status</th>
              <th className={`${t.wDate} ${t.hideSm}`}>Activated on</th>
              <th className={`${t.wMeter} ${t.hideXs}`}>Documents used</th>
              <th className={t.wLimit}>Licence limit</th>
              <th className={t.action} />
            </tr>
          </thead>
          <tbody>
            {docs.licences.map((licence) => {
              const row = tokenRows[licence.token_id] || {};
              const statusText = TOKEN_STATUS[licence.status] || licence.status;
              return (
                <tr key={licence.token_id}>
                  <td className={t.name} title={licence.name}>{licence.name}</td>
                  <td className={`${t.fit} ${t.hideXs}`}>
                    <span className={`${t.badge} ${licence.status === 'used' ? t.badgeSuccess : t.badgeWarning}`} title={statusText}>
                      {statusText}
                    </span>
                  </td>
                  <td className={t.hideSm} title={row.used_hostname || ''}>{row.used_hostname || <span className={t.muted}>—</span>}</td>
                  <td className={`${t.fit} ${t.hideXs}`}>
                    <UsageMeter
                      size="sm"
                      used={licence.documents_used}
                      limit={licence.document_limit}
                      unlimited={docs.unlimited}
                    />
                  </td>
                  <td className={t.fit}>
                    {docs.unlimited
                      ? <span className={`${t.badge} ${t.badgeSuccess}`}>Unlimited</span>
                      : isTrial
                        ? <span className={t.muted}>{formatNumber(licence.document_limit)} (trial)</span>
                        : (
                          <LimitEditor
                            value={licence.document_limit}
                            min={licence.documents_used}
                            onSave={(value) => onSaveLicence(licence, value)}
                            label={`Document limit for ${licence.name}`}
                          />
                        )}
                  </td>
                  <td className={t.action}>
                    <Button size="sm" variant="secondary" className={t.softButton} onClick={() => onViewUsage(licence.token_id)}>View usage</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!docs.licences.length && (
          <div className={styles.emptyState}>
            <strong>No licence tokens generated yet</strong>
            <span>
              {isTrial
                ? 'The free trial licence appears here once the customer generates its token.'
                : docs.unlimited
                  ? 'Licences of this organization can sign without a limit.'
                  : `Each new licence gets ${formatNumber(docs.suggested_allocation)} documents unless the customer chooses otherwise.`}
            </span>
          </div>
        )}
      </div>
      {!isTrial && !docs.unlimited && docs.licences.length > 0 && (
        <p className={styles.hint}>
          Raising a licence above what the organization has left also raises the organization limit by the difference.
        </p>
      )}
    </section>
  );
}

export default function ProductAdminCompany() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Expired or missing admin session: back to the login page
  const handleAuthError = useCallback((err) => {
    if ([401, 403].includes(err.response?.status)) {
      localStorage.removeItem('productAdminToken');
      navigate('/product-admin/login', { replace: true });
      return true;
    }
    return false;
  }, [navigate]);

  const load = useCallback(() => (
    productAdminService.company(companyId)
      .then(response => setReport(response.data.data))
      .catch(err => {
        if (!handleAuthError(err)) setError(err.response?.data?.error || 'Unable to load company details');
      })
      .finally(() => setLoading(false))
  ), [companyId, handleAuthError]);

  useEffect(() => { load(); }, [load]);

  // Run an admin change, show the result and reload; resolves true when it worked
  const run = async (request, success, failure) => {
    try {
      await request();
      toast.success(success);
      await load();
      return true;
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.error || failure);
      return false;
    }
  };

  const saveOrganizationLimit = (subscription, value) => run(
    () => productAdminService.setSubscriptionLimit(subscription.id, value),
    'Organization document limit updated',
    'Could not update the organization limit'
  );

  const makeUnlimited = (subscription) => {
    const name = report?.company?.company_name || 'this organization';
    if (!window.confirm(`Give ${name} unlimited documents until its plan ends?`)) return Promise.resolve(false);
    return run(
      () => productAdminService.setSubscriptionUnlimited(subscription.id),
      'Organization now has unlimited documents',
      'Could not give unlimited documents'
    );
  };

  const saveValidity = (subscription, payload) => run(
    () => productAdminService.setSubscriptionValidity(subscription.id, payload),
    'Plan validity updated',
    'Could not update the plan validity'
  );

  const saveLicenceLimit = (licence, value) => run(
    () => productAdminService.setTokenLimit(licence.token_id, value),
    `Document limit for ${licence.name} updated`,
    'Could not update the licence limit'
  );

  const admins = report?.admins || [];
  const tokenRows = Object.fromEntries((report?.tokens || []).map(token => [token.token_id, token]));

  return (
    <div className={ui.container}>
      <header className={ui.pageHeader}>
        <Button variant="secondary" size="sm" onClick={() => navigate('/product-admin/organizations')}>← Organizations</Button>
        <span className={ui.eyebrow}>ORGANIZATION</span>
        <h1 className={ui.title} title={report?.company?.company_name || ''}>{report?.company?.company_name || 'Company details'}</h1>
        <p className={ui.subtitle}>Plan, document limits, licences and signing activity.</p>
      </header>

      {loading && <section className={ui.panel}><p className={styles.loading}>Loading company details…</p></section>}
      {error && <div className={styles.error}>{error}</div>}

      {!loading && report && <>
        <section className={ui.panel}>
          <SectionHeading tone="blue" icon={<UsersIcon />} title="Company Contacts" />
          <div className={styles.contactGrid}>
            {admins.map(admin => (
              <div className={styles.contactCard} key={admin.id}>
                <strong title={admin.name || ''}>{admin.name || '—'}</strong>
                <span title={admin.email || ''}>{admin.email || '—'}</span>
                <span title={admin.phone_number || ''}>{admin.phone_number || '—'}</span>
                <small>Company admin</small>
              </div>
            ))}
          </div>
        </section>

        {(report.subscriptions || []).map(subscription => (
          <SubscriptionLimits
            key={subscription.id}
            subscription={subscription}
            tokenRows={tokenRows}
            onSaveLimit={saveOrganizationLimit}
            onMakeUnlimited={makeUnlimited}
            onSaveValidity={saveValidity}
            onSaveLicence={saveLicenceLimit}
            onViewUsage={(tokenId) => navigate(`/product-admin/token/${tokenId}`)}
          />
        ))}
        {Array.isArray(report.subscriptions) && !report.subscriptions.length && (
          <section className={ui.panel}>
            <div className={styles.emptyState}>
              <strong>No subscription yet</strong>
              <span>Document limits appear here once this organization starts a trial or buys a plan.</span>
            </div>
          </section>
        )}

        <section className={ui.panel}>
          <SectionHeading
            tone="purple"
            icon={<ActivityIcon />}
            title="Signing Activity"
            text="All tokens of this organization, including earlier subscriptions."
          />
          <div className={t.wrap}>
            <table className={t.table}>
              <thead>
                <tr>
                  <th>Token</th>
                  <th className={`${t.wStatus} ${t.hideXs}`}>Status</th>
                  <th className={`${t.wDate} ${t.hideSm}`}>Created</th>
                  <th className={`${t.wStat} ${t.num} ${t.hideSm}`}>Signing runs</th>
                  <th className={`${t.wStat} ${t.num}`}>Files</th>
                  <th className={`${t.wStat} ${t.num} ${t.hideXs}`}>Signatures</th>
                  <th className={t.action} />
                </tr>
              </thead>
              <tbody>
                {(report.tokens || []).map(token => {
                  const statusText = TOKEN_STATUS[token.status] || token.status;
                  const createdText = token.created_at ? formatDate(token.created_at) : '—';
                  const tokenName = token.name || String(token.token_id);
                  return (
                    <tr key={token.token_id}>
                      <td className={t.name} title={tokenName}>{tokenName}</td>
                      <td className={`${t.fit} ${t.hideXs}`}>
                        <span className={`${t.badge} ${token.status === 'used' ? t.badgeSuccess : t.badgeWarning}`} title={statusText}>{statusText}</span>
                      </td>
                      <td className={t.hideSm} title={createdText}>{createdText}</td>
                      <td className={`${t.num} ${t.hideSm}`} title={formatNumber(token.signing_events)}>{formatNumber(token.signing_events)}</td>
                      <td className={t.num} title={formatNumber(token.signed_files)}>{formatNumber(token.signed_files)}</td>
                      <td className={`${t.num} ${t.hideXs}`} title={formatNumber(token.signature_count)}>{formatNumber(token.signature_count)}</td>
                      <td className={t.action}>
                        {Number.isInteger(token.token_id) && (
                          <Button size="sm" variant="secondary" className={t.softButton} onClick={() => navigate(`/product-admin/token/${token.token_id}`)}>
                            View usage
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!(report.tokens || []).length && (
              <div className={styles.emptyState}>
                <strong>No signing activity yet</strong>
                <span>Activity appears here when this organization signs documents.</span>
              </div>
            )}
          </div>
        </section>
      </>}
    </div>
  );
}
